import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  format,
  formatDistance,
  isSameDay,
  startOfToday,
  startOfTomorrow,
  startOfYesterday,
} from "date-fns";
import { fr } from "date-fns/locale";
import z, { ZodTypeAny } from "zod";
import queryString from "query-string";

const isoDateOnlyRegex = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export function capitalize(str: string): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function deserializeURLSearchParams(query: string) {
  const parsed = queryString.parse(query, { arrayFormat: "bracket" });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value === "string" && value.includes(":")) {
      const [innerKey, innerValue] = value.split(":", 2);
      result[key] = { [innerKey]: innerValue };
    } else {
      result[key] = value;
    }
  }

  return result;
}

export function formatDate(date: Date | string) {
  return format(date, "PPPP", { locale: fr });
}

export function formatTime(date: Date | string) {
  return format(date, "HH 'h' mm", { locale: fr });
}

export function formatRelativeDate(date: Date | string) {
  const today = startOfToday();
  const tomorrow = startOfTomorrow();
  const yesterday = startOfYesterday();

  if (isSameDay(date, today)) return "aujourd'hui";

  if (isSameDay(date, tomorrow)) return "demain";

  if (isSameDay(date, yesterday)) return "hier";

  return capitalize(
    formatDistance(date, today, { locale: fr, addSuffix: true }),
  );
}

export function getFirstName(name: string): string {
  return name.trim().split(/\s+/)[0];
}

export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((word) => word[0]?.toLocaleUpperCase())
    .join("");
}

export function getLocalStorage<T>(key: string): T | null {
  if (typeof window === "undefined") return null; // server guard

  try {
    const stored = localStorage.getItem(key);
    return stored ? (JSON.parse(stored) as T) : null;
  } catch (error) {
    console.error("Error reading localStorage:", error);
    return null;
  }
}

export function interpolatePath(
  endpoint: string,
  pathParams: Record<string, string | number>,
) {
  return endpoint.replace(/\[([^\]]+)\]/g, (_, key) => {
    const value = pathParams[key];
    if (value === undefined) {
      throw new Error(`Missing path param: ${key}`);
    }
    return encodeURIComponent(String(value));
  });
}

export function removeAccents(str: string) {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function serializeURLSearchParams(object: Record<string, any>): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const transformed: Record<string, any> = {};

  for (const [key, value] of Object.entries(object)) {
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      const entries = Object.entries(value);
      if (entries.length === 1) {
        const [innerKey, innerValue] = entries[0];
        transformed[key] = `${innerKey}:${innerValue}`;
        continue;
      }
    }

    transformed[key] = value;
  }

  return queryString.stringify(transformed, { arrayFormat: "bracket" });
}

export function setLocalStorage<T>(key: string, value: T) {
  if (typeof window === "undefined") return; // server guard

  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error("Error writing localStorage:", error);
  }
}

export function stopEventPropagation() {
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return {
    // Clipboard Events
    onCopy: stop,
    onCut: stop,
    onPaste: stop,

    // Composition Events
    onCompositionEnd: stop,
    onCompositionStart: stop,
    onCompositionUpdate: stop,

    // Focus Events
    onFocus: stop,
    onBlur: stop,

    // Form Events
    onChange: stop,
    onInput: stop,
    onReset: stop,
    onSubmit: stop,
    onInvalid: stop,

    // Keyboard Events
    onKeyDown: stop,
    onKeyPress: stop,
    onKeyUp: stop,

    // Mouse Events
    onClick: stop,
    onContextMenu: stop,
    onDoubleClick: stop,
    onDrag: stop,
    onDragEnd: stop,
    onDragEnter: stop,
    onDragExit: stop,
    onDragLeave: stop,
    onDragOver: stop,
    onDragStart: stop,
    onDrop: stop,
    onMouseDown: stop,
    onMouseEnter: stop,
    onMouseLeave: stop,
    onMouseMove: stop,
    onMouseOut: stop,
    onMouseOver: stop,
    onMouseUp: stop,

    // Selection
    onSelect: stop,

    // Touch Events
    onTouchCancel: stop,
    onTouchEnd: stop,
    onTouchMove: stop,
    onTouchStart: stop,

    // Pointer Events
    onPointerDown: stop,
    onPointerMove: stop,
    onPointerUp: stop,
    onPointerCancel: stop,
    onPointerEnter: stop,
    onPointerLeave: stop,
    onPointerOver: stop,
    onPointerOut: stop,
    onGotPointerCapture: stop,
    onLostPointerCapture: stop,

    // UI Events
    onScroll: stop,
    onWheel: stop,

    // Animation Events
    onAnimationStart: stop,
    onAnimationEnd: stop,
    onAnimationIteration: stop,
    onTransitionEnd: stop,
  };
}

export function toggleItem<T>(array: T[], item: T): T[] {
  return array.includes(item)
    ? array.filter((i) => i !== item)
    : [...array, item];
}

export function validateParams<T extends ZodTypeAny>(
  schema: T,
  params: unknown,
): z.infer<T> {
  const result = schema.safeParse(params);

  if (!result.success) {
    throw new Error(`Invalid parameters: ${result.error.message}`);
  }

  return result.data;
}

export function zodBoolean() {
  return z.any().transform((value): boolean => {
    if (typeof value === "boolean") return value;

    const str = String(value).toLowerCase().trim();

    if (["true", "1", "yes", "on"].includes(str)) return true;
    if (["false", "0", "no", "off"].includes(str)) return false;

    return false;
  });
}

// TODO: find a way to set the type to be DateOnlyString
export function zodISODateOnlyString() {
  return z.string().regex(isoDateOnlyRegex, {
    message: "Invalid ISO 8601 date string (expected yyyy-mm-dd)",
  });
}

export function zodSortField<T extends string>(allowedFields: readonly T[]) {
  return z
    .record(z.string(), z.string())
    .optional()
    .refine(
      (obj) => {
        if (!obj) return true; // allow undefined
        const keys = Object.keys(obj);
        return (
          keys.length === 1 &&
          allowedFields.includes(keys[0] as T) &&
          ["asc", "desc"].includes(obj[keys[0]] as string)
        );
      },
      { message: "Invalid sort field or direction" },
    )
    .transform((value) => {
      if (!value) return undefined;
      const [field] = Object.keys(value);
      const direction = value[field];
      return { [field]: direction } as Record<T, "asc" | "desc">;
    });
}

export function formatExecutionTime(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(2)} s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes} min ${remainingSeconds} s`;
}

export function omit<T extends object, K extends keyof T>(
  obj: T,
  keys: K[],
): Omit<T, K> {
  const clone = { ...obj };
  keys.forEach((k) => delete clone[k]);
  return clone;
}

export function chunkArray<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];

  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }

  return chunks;
}

export function createOptions<T extends Record<string, string>>(obj: T) {
  return Object.values(obj).map((v) => ({
    label: v,
    value: v,
  }));
}

export function formatDisplayDate(
  date?: string | Date | null,
  withTime = false,
) {
  if (!date) return "-";

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "short",
    ...(withTime && { timeStyle: "short" }),
  }).format(new Date(date));
}

export type FormatDecimalOptions = Intl.NumberFormatOptions & {
  unit?: string;
};

export function formatDecimal(
  value: number | string | null | undefined,
  options?: FormatDecimalOptions,
): string {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number =
    typeof value === "string" ? Number(value.replace(",", ".")) : value;

  if (Number.isNaN(number)) {
    return "-";
  }

  const { unit, ...formatOptions } = options ?? {};

  const formatted = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...formatOptions,
  }).format(number);

  return unit ? `${formatted} ${unit}` : formatted;
}
export function formatCurrency(value: number | null | undefined): string {
  if (value == null) {
    return "-";
  }

  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(value);
}
